/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useEffect, useState } from 'react';
import { Modal } from '../../modals/Modal';
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as FileSystem from 'expo-file-system/legacy';
import { spacing } from '../../theme/spacing'
import { typography } from '../../theme/typography'
import { ticketColors } from '../../theme/ticketColors';
import { useModalBackdropPadding } from '../../ui/hooks-ui/useModalBackdropPadding';

type Props = {
  visible: boolean;
  uri: string | null;
  fileName: string;
  mimeType: string;
  onClose: () => void;
};

/**
 * Data URIs (data:...) no funcionan con Linking.openURL en nativo.
 * Si el uri es un data URI, escribe el contenido a un archivo temporal
 * y devuelve el path file:// para que pueda abrirse. En web, devuelve
 * el uri tal cual (los navegadores sí soportan data URIs).
 */
async function resolveOpenableUri(uri: string, fileName: string, mimeType: string): Promise<string | null> {
  if (!uri.startsWith('data:')) return uri;
  if (Platform.OS === 'web') return uri;
  const commaIdx = uri.indexOf(',');
  if (commaIdx < 0) return null;
  const base64 = uri.slice(commaIdx + 1);
  const ext = mimeType === 'application/pdf' ? '.pdf' : '';
  const safeName = (fileName || 'comprobante').replace(/[^a-zA-Z0-9._-]/g, '_');
  const fileUri = `${FileSystem.cacheDirectory}${safeName}${ext}`;
  await FileSystem.writeAsStringAsync(fileUri, base64, { encoding: 'base64' as const });
  return fileUri;
}

export function AttachmentPreviewModal({ visible, uri, fileName, mimeType, onClose }: Props) {
  const backdropPadding = useModalBackdropPadding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      setError(null);
    }
  }, [visible]);

  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      accessibilityViewIsModal
    >
      <View style={styles.overlay}>
        <View style={[styles.sheet, backdropPadding]}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Ionicons
                name={isPdf ? 'document-text-outline' : 'image-outline'}
                size={18}
                color={ticketColors.ink2}
              />
              <Text style={styles.headerTitle} numberOfLines={1}>
                {fileName}
              </Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
              style={styles.closeBtn}
            >
              <Ionicons name="close" size={22} color={ticketColors.ink2} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={styles.content}>
            {!uri && !error ? (
              <View style={styles.errorBox}>
                <Ionicons name="alert-circle-outline" size={32} color={ticketColors.ink2} />
                <Text style={styles.errorText}>Comprobante no disponible.</Text>
              </View>
            ) : null}

            {loading && isImage && uri ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={ticketColors.gold} />
                <Text style={styles.loadingText}>Cargando comprobante…</Text>
              </View>
            ) : null}

            {error && (
              <View style={styles.errorBox}>
                <Ionicons name="alert-circle-outline" size={32} color={ticketColors.bad} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            )}

            {!error && uri && isImage && (
              <ScrollView
                style={styles.scrollArea}
                contentContainerStyle={styles.scrollContent}
                maximumZoomScale={3}
                minimumZoomScale={1}
              >
                <Image
                  source={{ uri }}
                  style={styles.image}
                  resizeMode="contain"
                  onLoadEnd={() => setLoading(false)}
                  onError={() => {
                    setLoading(false);
                    setError('No pude cargar la imagen.');
                  }}
                />
              </ScrollView>
            )}

            {!error && uri && isPdf && Platform.OS === 'web' && (
              <iframe
                src={uri}
                title={fileName}
                style={{ width: '100%', height: '100%', minHeight: 400, border: 'none' }}
                onLoad={() => setLoading(false)}
              />
            )}

            {!error && uri && isPdf && Platform.OS !== 'web' && (
              <View style={styles.pdfFallback}>
                <Ionicons name="document-text-outline" size={48} color={ticketColors.ink2} />
                <Text style={styles.pdfFallbackText}>
                  PDF no se puede previsualizar en la app móvil.
                </Text>
                <TouchableOpacity
                  onPress={async () => {
                    try {
                      const target = await resolveOpenableUri(uri, fileName, mimeType);
                      if (target) await Linking.openURL(target);
                    } catch {
                      setError('No pude abrir el archivo. Probá de nuevo cuando se sincronice.');
                    }
                  }}
                  style={styles.openExternalBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Abrir en navegador"
                >
                  <Text style={styles.openExternalText}>Abrir en navegador</Text>
                </TouchableOpacity>
              </View>
            )}

            {!error && uri && !isImage && !isPdf ? (
              <View style={styles.pdfFallback}>
                <Ionicons name="document-outline" size={48} color={ticketColors.ink2} />
                <Text style={styles.pdfFallbackText}>
                  Este archivo no se puede previsualizar acá.
                </Text>
                <TouchableOpacity
                  onPress={async () => {
                    try {
                      const target = await resolveOpenableUri(uri, fileName, mimeType);
                      if (target) await Linking.openURL(target);
                    } catch {
                      setError('No pude abrir el archivo. Probá de nuevo cuando se sincronice.');
                    }
                  }}
                  style={styles.openExternalBtn}
                  accessibilityRole="button"
                  accessibilityLabel="Abrir afuera"
                >
                  <Text style={styles.openExternalText}>Abrir afuera</Text>
                </TouchableOpacity>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sheet: {
    width: '90%',
    maxWidth: 640,
    maxHeight: '85%',
    backgroundColor: ticketColors.paper,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  headerTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
    flex: 1,
  },
  closeBtn: {
    padding: spacing.xs,
  },
  content: {
    flex: 1,
    minHeight: 300,
  },
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  image: {
    width: '100%',
    minHeight: 280,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
  },
  loadingBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  loadingText: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  errorBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.xl,
  },
  errorText: {
    ...typography.body,
    color: ticketColors.bad,
    textAlign: 'center',
  },
  pdfFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    padding: spacing.xl,
  },
  pdfFallbackText: {
    ...typography.body,
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  openExternalBtn: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  openExternalText: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '600',
  },
});
